import React, { useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { IProviderListDTO } from 'features/providers/types';
import { copyCostIssuingRules } from '../../services';
import { IssueCostCondition, IssueCostRuleCopyResult } from '../../types';
import { describeRuleWriteError, rulesCount } from '../../helpers';

interface Props {
	rules: IssueCostCondition[];
	providers: IProviderListDTO[];
	onClose: () => void;
	onCopied: (message: string) => void;
}

/**
 * Copia las reglas de un proveedor a otro. Primero muestra cuántas copia (la API lo calcula sin escribir) y recién
 * con "Copiar" las escribe. Solo agrega las que el destino no tiene: nunca pisa ni borra las suyas. Se monta al
 * abrirse; mientras trabaja no se puede cambiar el par, así la vista previa siempre es la del par elegido.
 */
export function CopyCostIssuingRulesDialog({ rules, providers, onClose, onCopied }: Props): JSX.Element {
	const [from, setFrom] = useState<IProviderListDTO | null>(null);
	const [to, setTo] = useState<IProviderListDTO | null>(null);
	const [preview, setPreview] = useState<IssueCostRuleCopyResult | null>(null);
	const [working, setWorking] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);

	// Origen: solo los proveedores que tienen reglas, con cuántas.
	const rulesByProvider = useMemo(() => {
		const counts = new Map<string, number>();
		rules.forEach((rule) => counts.set(rule.providerId, (counts.get(rule.providerId) ?? 0) + 1));
		return counts;
	}, [rules]);
	const sources = useMemo(
		() => providers.filter((provider) => rulesByProvider.has(provider.id)),
		[providers, rulesByProvider],
	);

	const run = async (dryRun: boolean) => {
		if (!from || !to) return;
		setWorking(true);
		setApiError(null);
		try {
			const { data } = await copyCostIssuingRules({ fromProviderId: from.id, toProviderId: to.id, dryRun });
			if (dryRun) {
				setPreview(data);
				setWorking(false);
			} else
				onCopied(
					`Se copiaron ${rulesCount(data.copied)} de ${from.fantasyName} a ${to.fantasyName}` +
						(data.skipped > 0 ? ` (${rulesCount(data.skipped)} ya las tenía)` : ''),
				);
		} catch (error) {
			setApiError(describeRuleWriteError(error, 'No se pudieron copiar las reglas'));
			setWorking(false);
		}
	};

	const choose = (setter: (value: IProviderListDTO | null) => void) => (value: IProviderListDTO | null) => {
		setter(value);
		setPreview(null);
		setApiError(null);
	};

	const samePair = from !== null && to !== null && from.id === to.id;

	return (
		<Dialog open onClose={working ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Duplicar reglas de un proveedor a otro</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<Autocomplete
						options={sources}
						value={from}
						getOptionLabel={(option) => `${option.fantasyName} (${rulesCount(rulesByProvider.get(option.id) ?? 0)})`}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(_, value) => choose(setFrom)(value)}
						noOptionsText="Ningún proveedor con ese nombre tiene reglas"
						disabled={working}
						renderInput={(params) => <TextField {...params} size="small" label="Copiar las reglas de" />}
					/>
					<Autocomplete
						options={providers}
						value={to}
						getOptionLabel={(option) => option.fantasyName}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(_, value) => choose(setTo)(value)}
						noOptionsText="No hay proveedores con ese nombre"
						disabled={working}
						renderInput={(params) => (
							<TextField
								{...params}
								size="small"
								label="A este proveedor"
								error={samePair}
								helperText={samePair ? 'Elegí otro proveedor' : undefined}
							/>
						)}
					/>
					<Typography variant="body2" color="text.secondary">
						Solo se agregan las reglas que el destino no tiene (misma aerolínea y mismo tipo de tarifa). Las que ya
						tiene no se tocan.
					</Typography>
					{preview && from && to && (
						<Alert severity={preview.copied > 0 ? 'info' : 'warning'}>
							{preview.copied > 0
								? `Se van a copiar ${rulesCount(preview.copied)} de las ${preview.sourceRules} de ${
										from.fantasyName
								  } a ${to.fantasyName}.`
								: `${to.fantasyName} ya tiene todas las reglas de ${from.fantasyName}: no hay nada para copiar.`}
							{preview.skipped > 0 && ` ${rulesCount(preview.skipped)} se saltean porque ya las tiene.`}
						</Alert>
					)}
					{apiError && <Alert severity="error">{apiError}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={working}>
					Cancelar
				</Button>
				{preview && preview.copied > 0 ? (
					<Button
						variant="contained"
						onClick={() => run(false)}
						disabled={working}
						startIcon={working ? <CircularProgress size={16} /> : undefined}
					>
						Copiar {rulesCount(preview.copied)}
					</Button>
				) : (
					<Button
						variant="contained"
						onClick={() => run(true)}
						disabled={working || !from || !to || samePair}
						startIcon={working ? <CircularProgress size={16} /> : undefined}
					>
						Ver qué se copia
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
